import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { odooApiService, OdooProduct, OdooInventoryData } from '../../services/odooApi'

interface OdooState {
  inventory: OdooInventoryData | null
  selectedProduct: OdooProduct | null
  loading: boolean
  error: string | null
  lastSync: string | null
  connectionStatus: 'idle' | 'connecting' | 'connected' | 'failed'
  syncInProgress: boolean
}

const initialState: OdooState = {
  inventory: null,
  selectedProduct: null,
  loading: false,
  error: null,
  lastSync: null,
  connectionStatus: 'idle',
  syncInProgress: false
}

// Async thunks for Odoo API calls
export const testOdooConnection = createAsyncThunk(
  'odoo/testConnection',
  async (_, { rejectWithValue }) => {
    try {
      const isConnected = await odooApiService.testConnection()
      return isConnected
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Connection test failed')
    }
  }
)

export const fetchOdooInventory = createAsyncThunk(
  'odoo/fetchInventory',
  async (_, { rejectWithValue }) => {
    try {
      const inventoryData = await odooApiService.getInventoryData()
      return inventoryData
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch inventory')
    }
  }
)

export const fetchOdooProduct = createAsyncThunk(
  'odoo/fetchProduct',
  async (payload: number | { productId: number, forceRefresh?: boolean }, { rejectWithValue }) => {
    try {
      const productId = typeof payload === 'number' ? payload : payload.productId
      const forceRefresh = typeof payload === 'object' ? payload.forceRefresh : false
      
      const result = await odooApiService.getProduct(productId, forceRefresh)
      if (!result) {
        throw new Error('Product not found')
      }
      return result
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch product')
    }
  }
)

export const searchOdooProducts = createAsyncThunk(
  'odoo/searchProducts',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      const products = await odooApiService.searchProducts(searchTerm)
      return products
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to search products')
    }
  }
)

export const updateOdooProductQuantity = createAsyncThunk(
  'odoo/updateProductQuantity',
  async ({ productId, quantity }: { productId: number; quantity: number }, { rejectWithValue }) => {
    try {
      const success = await odooApiService.updateProductQuantity(productId, quantity)
      return { productId, quantity, success }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update quantity')
    }
  }
)

export const updateOdooProductPrice = createAsyncThunk(
  'odoo/updateProductPrice',
  async ({ productId, price }: { productId: number; price: number }, { rejectWithValue }) => {
    try {
      const success = await odooApiService.updateProductPrice(productId, price)
      return { productId, price, success }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update price')
    }
  }
)

const odooSlice = createSlice({
  name: 'odoo',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null
    },
    setConnectionStatus: (state, action: PayloadAction<OdooState['connectionStatus']>) => {
      state.connectionStatus = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Test connection
      .addCase(testOdooConnection.pending, (state) => {
        state.connectionStatus = 'connecting'
        state.error = null
      })
      .addCase(testOdooConnection.fulfilled, (state, action) => {
        state.connectionStatus = action.payload ? 'connected' : 'failed'
        if (!action.payload) {
          state.error = 'Connection test failed'
        }
      })
      .addCase(testOdooConnection.rejected, (state, action) => {
        state.connectionStatus = 'failed'
        state.error = action.payload as string
      })

      // Fetch inventory
      .addCase(fetchOdooInventory.pending, (state) => {
        state.loading = true
        state.syncInProgress = true
        state.error = null
      })
      .addCase(fetchOdooInventory.fulfilled, (state, action) => {
        state.loading = false
        state.syncInProgress = false
        state.inventory = action.payload
        state.lastSync = new Date().toISOString()
        state.connectionStatus = 'connected'
      })
      .addCase(fetchOdooInventory.rejected, (state, action) => {
        state.loading = false
        state.syncInProgress = false
        state.error = action.payload as string
        state.connectionStatus = 'failed'
      })

      // Fetch single product
      .addCase(fetchOdooProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOdooProduct.fulfilled, (state, action) => {
        state.loading = false
        state.selectedProduct = action.payload
      })
      .addCase(fetchOdooProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Search products
      .addCase(searchOdooProducts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(searchOdooProducts.fulfilled, (state, action) => {
        state.loading = false
        // Update inventory with search results
        if (state.inventory) {
          state.inventory.products = action.payload
          state.inventory.totalCount = action.payload.length
          state.inventory.lastUpdated = new Date().toISOString()
        } else {
          state.inventory = {
            products: action.payload,
            totalCount: action.payload.length,
            lastUpdated: new Date().toISOString()
          }
        }
      })
      .addCase(searchOdooProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Update product quantity
      .addCase(updateOdooProductQuantity.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateOdooProductQuantity.fulfilled, (state, action) => {
        state.loading = false
        // Update the product in inventory if it exists
        if (state.inventory) {
          const productIndex = state.inventory.products.findIndex(p => p.id === action.payload.productId)
          if (productIndex !== -1) {
            state.inventory.products[productIndex].qty_available = action.payload.quantity
          }
        }
        // Update selected product if it matches
        if (state.selectedProduct && state.selectedProduct.id === action.payload.productId) {
          state.selectedProduct.qty_available = action.payload.quantity
        }
      })
      .addCase(updateOdooProductQuantity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

      // Update product price
      .addCase(updateOdooProductPrice.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateOdooProductPrice.fulfilled, (state, action) => {
        state.loading = false
        // Update the product in inventory if it exists
        if (state.inventory) {
          const productIndex = state.inventory.products.findIndex(p => p.id === action.payload.productId)
          if (productIndex !== -1) {
            state.inventory.products[productIndex].list_price = action.payload.price
          }
        }
        // Update selected product if it matches
        if (state.selectedProduct && state.selectedProduct.id === action.payload.productId) {
          state.selectedProduct.list_price = action.payload.price
        }
      })
      .addCase(updateOdooProductPrice.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  }
})

export const { clearError, clearSelectedProduct, setConnectionStatus } = odooSlice.actions

// Selectors
export const selectOdooInventory = (state: { odoo: OdooState }) => state.odoo.inventory
export const selectOdooProducts = (state: { odoo: OdooState }) => state.odoo.inventory?.products || []
export const selectOdooLoading = (state: { odoo: OdooState }) => state.odoo.loading
export const selectOdooError = (state: { odoo: OdooState }) => state.odoo.error
export const selectOdooLastSync = (state: { odoo: OdooState }) => state.odoo.lastSync
export const selectOdooConnectionStatus = (state: { odoo: OdooState }) => state.odoo.connectionStatus
export const selectOdooSyncInProgress = (state: { odoo: OdooState }) => state.odoo.syncInProgress
export const selectOdooSelectedProduct = (state: { odoo: OdooState }) => state.odoo.selectedProduct

export default odooSlice.reducer
